import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';
import {
    ICostCentre,
    ICallState,
    IVoiceCommand,
    IVoiceOutgoing,
    IVoiceServers,
    IVoiceStunServers,
    IVoiceLogin,
    IOperatorCLIResponse,
    IVoiceIVRs,
    IOperatorRingtime,
    IVoiceUnMonitor,
    IVoicePayment,
    IVoicePaymentInformation,
    ISipState,
    IVoiceLog,
} from '@dxp/core/models';
import { environment } from '@dxp/core/environment';
import { LOGGER_REQUEST_NAME } from '@dxp/core/types';

@Injectable({
    providedIn: 'root',
})
export class VoiceApiService {
    readonly #http = inject(HttpClient);

    operatorCLI = signal<string>('');
    operatorRingtime = signal(0);
    costCentres = signal<ICostCentre[]>;

    changeCallState(params: ICallState) {
        this.#http
            .patch<void>(`${environment.voiceUrl}/call`, params, {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Voice Service (Change call state)',
                ),
            })
            .subscribe();
    }

    callCommand(params: IVoiceCommand) {
        this.#http
            .patch<void>(`${environment.voiceUrl}/call/command`, params, {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Voice Service (Transfer call)',
                ),
            })
            .subscribe();
    }

    makeOutgoingCall(outParams: IVoiceOutgoing): Observable<void> {
        return this.#http.patch<void>(
            `${environment.voiceUrl}/call/outgoing`,
            outParams,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Voice Service (Outgoing call)',
                ),
            },
        );
    }

    getAsteriskServers(): Observable<IVoiceServers> {
        return this.#http.get<IVoiceServers>(
            `${environment.voiceUrl}/data/asterisk/servers`,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Voice Service (Get Asterisk servers)',
                ),
            },
        );
    }

    getAsteriskStunServers(): Observable<IVoiceStunServers> {
        return this.#http.get<IVoiceStunServers>(
            `${environment.voiceUrl}/data/asterisk/stun`,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Voice Service (Get Asterisk stun servers)',
                ),
            },
        );
    }

    getAsteriskLogin(): Observable<IVoiceLogin> {
        return this.#http.get<IVoiceLogin>(
            `${environment.voiceUrl}/data/asterisk/user-info`,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Voice Service (Get Asterisk login)',
                ),
            },
        );
    }

    getOperatorCLI() {
        this.#http
            .get<IOperatorCLIResponse>(
                `${environment.voiceUrl}/data/operator/cli`,
                {
                    context: new HttpContext().set(
                        LOGGER_REQUEST_NAME,
                        'Voice Service (Get operator cli)',
                    ),
                },
            )
            .subscribe({
                next: response => {
                    if (response.isSuccess && response.data.length > 0) {
                        const cli = response.data[0].presCLI_value;
                        this.operatorCLI.set(cli);
                    } else {
                        this.operatorCLI.set('');
                    }
                },
                error: () => {
                    this.operatorCLI.set('');
                },
            });
    }

    getCostCentres(): Observable<ICostCentre[]> {
        return this.#http.get<ICostCentre[]>(
            `${environment.voiceUrl}/costcentre`,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Voice Service (Get cost centres)',
                ),
            },
        );
    }

    getIVRs(callId: number): Observable<IVoiceIVRs> {
        return this.#http.get<IVoiceIVRs>(
            `${environment.voiceUrl}/data/call/yoyo/${callId}`,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Voice Service (Get IVRs)',
                ),
            },
        );
    }

    getOperatorRingtime(): Observable<IOperatorRingtime> {
        return this.#http.get<IOperatorRingtime>(
            `${environment.voiceUrl}/data/operator/ringtime`,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Voice Service (Get operator ringtime)',
                ),
            },
        );
    }

    startVoiceMontior(conversationId: string): Observable<void> {
        return this.#http.get<void>(
            `${environment.voiceUrl}/monitor/start/${conversationId}`,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Voice Service (Start monitor)',
                ),
            },
        );
    }

    stopVoiceMontior(unMonitor: IVoiceUnMonitor): Observable<void> {
        return this.#http.post<void>(
            `${environment.voiceUrl}/monitor/stop`,
            unMonitor,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Voice Service (Stop monitor)',
                ),
            },
        );
    }

    processPayment(
        payment: IVoicePayment,
    ): Observable<IVoicePaymentInformation> {
        return this.#http.post<IVoicePaymentInformation>(
            `${environment.voiceUrl}/payment`,
            payment,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Voice Service (Process payment)',
                ),
            },
        );
    }

    completePayment(payment: IVoicePayment): Observable<void> {
        return this.#http.post<void>(
            `${environment.voiceUrl}/payment/complete`,
            payment,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Voice Service (Complete payment)',
                ),
            },
        );
    }

    sipState(state: string, params: ISipState) {
        this.#http
            .patch<void>(`${environment.voiceUrl}/sip/${state}`, params, {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Voice Service (Change sip state)',
                ),
            })
            .subscribe();
        this.log({
            agentId: params.agentId,
            tenantId: params.tenantId,
            errorMessage: `sip: ${state}`,
        });
    }

    log(params: IVoiceLog) {
        this.#http
            .post<void>(`${environment.voiceUrl}/log`, params, {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Voice Service (Log)',
                ),
            })
            .subscribe();
    }
}
